import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { WarningRounded } from "@material-ui/icons";
import {
  confirmClientRegistration,
  generatePasswordSetupLink as requestPasswordSetupLink,
  getClients,
} from "../../api";
import ModalConfirm from "../../components/ModalConfirm";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import * as S from "./Clientes.styles";

const FILTER_DEBOUNCE_MS = 400;

function getVerificationState(row) {
  if (row.verified && !row.first_access_required) {
    return { status: "Ativo", kind: "active" };
  }
  if (row.verified && row.first_access_required) {
    return { status: "E-mail ok, senha pendente", kind: "password_pending" };
  }
  return { status: "Pendente", kind: "pending" };
}

function getVerificationStatus(row) {
  return getVerificationState(row).status;
}

export function Clientes() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [actionClientId, setActionClientId] = useState(null);
  const [clientToConfirm, setClientToConfirm] = useState(null);
  const [confirmError, setConfirmError] = useState("");
  const [passwordSetupLink, setPasswordSetupLink] = useState(null);

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    document: "",
  });

  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
  });

  const debouncedName = useDebouncedValue(filters.name, FILTER_DEBOUNCE_MS);
  const debouncedEmail = useDebouncedValue(filters.email, FILTER_DEBOUNCE_MS);
  const debouncedDocument = useDebouncedValue(filters.document, FILTER_DEBOUNCE_MS);

  const requestIdRef = useRef(0);

  const fetchClients = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await getClients({
        page: pagination.page,
        perPage: pagination.perPage,
        name: debouncedName,
        email: debouncedEmail,
        document: debouncedDocument,
      });
      if (requestId !== requestIdRef.current) return;
      setData(response.data.data);
      setTotalRows(response.data.total);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const copyPasswordSetupLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch (_error) {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const openPasswordSetupLinkModal = (result) => {
    if (!result?.create_password_url || !result?.email) {
      return false;
    }

    setPasswordSetupLink({
      url: result.create_password_url,
      email: result.email,
      expiresIn: result.expires_in,
    });
    return true;
  };

  const closeConfirmModal = () => {
    if (actionClientId != null) {
      return;
    }
    setClientToConfirm(null);
    setConfirmError("");
  };

  const confirmRegistration = async () => {
    if (!clientToConfirm?.id || actionClientId != null) {
      return;
    }

    const clientId = clientToConfirm.id;
    setConfirmError("");
    setActionClientId(clientId);
    try {
      const { data: result } = await confirmClientRegistration(clientId);
      toast.success("Cadastro confirmado com sucesso!");
      setClientToConfirm(null);
      setConfirmError("");

      if (result?.first_access_required) {
        const opened = openPasswordSetupLinkModal(result);
        if (!opened) {
          toast.error(
            "Cadastro confirmado, mas o link de criação de senha não veio completo."
          );
        }
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível confirmar o cadastro.";
      setConfirmError(message);
      toast.error(message);
      return;
    } finally {
      setActionClientId((current) => (current === clientId ? null : current));
    }

    try {
      await fetchClients();
    } catch (_error) {
      toast.error("Cadastro confirmado, mas não foi possível atualizar a lista.");
    }
  };

  const handleGeneratePasswordSetupLink = async (clientId) => {
    if (actionClientId != null) {
      return;
    }

    setConfirmError("");
    setActionClientId(clientId);
    try {
      const { data: result } = await requestPasswordSetupLink(clientId);
      const opened = openPasswordSetupLinkModal(result);
      if (!opened) {
        toast.error(
          "A resposta do link de criação de senha veio incompleta."
        );
        return;
      }
      toast.success("Link de criação de senha gerado.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível gerar o link de criação de senha.";
      toast.error(message);
    } finally {
      setActionClientId((current) => (current === clientId ? null : current));
    }
  };

  const whatsapp = (fullName, phone, email) => {
    const firstName = fullName.split(" ")[0];
    const text = `Olá ${firstName}, tudo bem? Somos da plataforma jurídica Jusfy e o seu e-mail ${email} ainda não foi confirmado, este e-mail está correto? Podemos confirmar para liberar o seu acesso?`;
    const phoneOnlyNumbers = phone
      .replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .replace(" ", "");
    const phoneInternational = `55${phoneOnlyNumbers}`;

    window.open(
      `https://api.whatsapp.com/send?phone=${phoneInternational}&text=${text}`,
      "_blank"
    );
  };

  const columns = [
    {
      name: "Ação",
      cell: (row) => (
        <Link target="_BLANK" to={`/clientes/${row.id}`}>
          <button
            type="button"
            className={`btn btn-icon ${!row.deleted_at ? "btn-primary" : "btn-danger"}`}
          >
            <i className="flaticon-eye"></i>
          </button>
        </Link>
      ),
      width: "80px"
    },
    {
      name: "Cadastrado em",
      selector: row => row.created_at,
      width: "150px"
    },
    {
      name: "Nome",
      selector: row => row.name,
      width: "150px"
    },
    {
      name: "E-mail",
      selector: row => row.email,
      width: "200px",
    },
    {
      name: "Telefone",
      selector: row => row.phone,
      width: "150px"
    },
    {
      name: "CPF",
      selector: row => row.document_number,
      width: "150px"
    },
    {
      name: "Cidade",
      selector: row => `${row.city_name} - ${row.state}`
    },
    {
      name: "Profissão",
      selector: row => row.profession_name
    },
    {
      name: "Status",
      selector: row => getVerificationStatus(row),
      width: "180px",
    },
    {
      name: "Verificado",
      cell: (row) => {
        const { kind } = getVerificationState(row);

        if (kind === "active") {
          return "SIM";
        }

        if (kind === "password_pending") {
          return (
            <div>
              <button
                type="button"
                className="btn btn-primary mr-3"
                disabled={actionClientId != null}
                onClick={() => handleGeneratePasswordSetupLink(row.id)}
              >
                {actionClientId === row.id
                  ? "GERANDO..."
                  : "GERAR LINK SENHA"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => whatsapp(row.name, row.phone, row.email)}
              >
                WHATSAPP
              </button>
            </div>
          );
        }

        return (
          <div>
            <button
              type="button"
              className="btn btn-success mr-3"
              disabled={actionClientId != null}
              onClick={() => {
                setConfirmError("");
                setClientToConfirm(row);
              }}
            >
              CONFIRMAR CADASTRO
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => whatsapp(row.name, row.phone, row.email)}
            >
              WHATSAPP
            </button>
          </div>
        );
      },
      width: "360px",
    }
  ];

  useEffect(() => {
    fetchClients();
  }, [
    pagination.page,
    pagination.perPage,
    debouncedName,
    debouncedEmail,
    debouncedDocument,
  ]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePerPageChange = (newPerPage, page) => {
    setPagination({ page, perPage: newPerPage });
  };

  const subHeaderComponent = useMemo(() => (
    <S.FiltersContainer>
      <p>Filtrar por:</p>
      <S.FiltersWrapper>
        <div>
          <S.Input
            type="text"
            placeholder="Nome"
            className="form-control"
            value={filters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
          />
        </div>
        <div>
          <S.Input
            type="text"
            placeholder="Email"
            className="form-control"
            value={filters.email}
            onChange={(e) => handleFilterChange("email", e.target.value)}
          />
        </div>
        <div>
          <S.Input
            type="text"
            placeholder="CPF/CNPJ"
            className="form-control"
            value={filters.document}
            onChange={(e) =>
              handleFilterChange("document", e.target.value.replace(/\D/g, ""))
            }
          />
        </div>
      </S.FiltersWrapper>
    </S.FiltersContainer>
  ), [filters]);

  const isActing = actionClientId != null;

  const passwordSetupMessage = passwordSetupLink
    ? [
        `Envie este link para ${passwordSetupLink.email}.`,
        passwordSetupLink.expiresIn
          ? `Válido por ${passwordSetupLink.expiresIn}.`
          : null,
        "Se expirar ou o cliente não criar a senha, gere novamente.",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <>
      <h1 className="azul-cabecalho">CLIENTES</h1>
      <p className="subheader-text mb-10">Listagem de todos clientes</p>
      <Link to="/clientes/add">
        <button
          type="button"
          className="btn btn-icon btn-primary mb-10"
          style={{ width: "200px" }}

        >
          <i className="flaticon2-add"></i>&nbsp;&nbsp;NOVO CLIENTE
        </button>
      </Link>

      <div className="row mb-10">
        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerPageChange}
          subHeader
          subHeaderComponent={subHeaderComponent}
        />
      </div>

      {clientToConfirm && (
        <ModalConfirm
          icon={WarningRounded}
          title="Confirmar cadastro?"
          message={`O e-mail de ${clientToConfirm.name || "este cliente"} será marcado como verificado. Se estiver no primeiro acesso, geraremos um link de criação de senha para você copiar e enviar (ex.: WhatsApp).`}
          onConfirm={confirmRegistration}
          onClose={closeConfirmModal}
          isLoading={isActing}
        >
          {confirmError ? (
            <p style={{ color: "#f42a3f", marginTop: 8 }}>{confirmError}</p>
          ) : null}
        </ModalConfirm>
      )}

      {passwordSetupLink && (
        <ModalConfirm
          icon={WarningRounded}
          title="Link de criação de senha"
          message={passwordSetupMessage}
          oneButtonMessage="Fechar"
          onConfirm={() => setPasswordSetupLink(null)}
          onClose={() => setPasswordSetupLink(null)}
        >
          <div style={{ textAlign: "left", marginTop: 8 }}>
            <input
              className="form-control"
              readOnly
              value={passwordSetupLink.url}
              style={{ fontSize: 12, marginBottom: 12 }}
              onFocus={(event) => event.target.select()}
            />
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => copyPasswordSetupLink(passwordSetupLink.url)}
            >
              Copiar link
            </button>
          </div>
        </ModalConfirm>
      )}
    </>
  );
}
